import { Client, Report } from "@bugsnag/browser/dist/types/bugsnag-core";
import bugsnag from "@bugsnag/js";
import { INotifyOpts } from "@bugsnag/node/dist/types/bugsnag-core/common";
import bugsnagReact from "@bugsnag/plugin-react";
import React from "react";
import { actionLog, Store } from "src/store";
import { errorStateSanitizer } from "src/store/stateSanitizer";

const BUGSNAG_APP_ID = "f427cbe1339f65528f783cf5d1687e0e";
const notifyReleaseStages = ["prod", "demo", "test"];
const ERRORS_TO_IGNORE = [
    "NetworkError when attempting to fetch resource.",
    "cancelled",
    "Aborted",
    "Failed to fetch",
    "Network request failed",
];

export let bugsnagClient: Client;

const determineIsOurError = (report: Report) => {
    const stackTrace = report.stacktrace || [];

    if (stackTrace.length === 0) {
        return false;
    }

    return stackTrace.reduce((isOurs: boolean, s: any) => {
        return isOurs || (s.file && ["localhost", "onlineshopper.dealerinspire"].some((f) => s.file.includes(f)));
    }, false);
};

const loadStateBeforeErrorSubmit = (store: Store) => (report: Report) => {
    if (!determineIsOurError(report)) {
        report.ignore();
        return;
    }

    if (report.errorClass === "ChunkLoadError") {
        report.ignore();
        return;
    }

    const currentStore = store.getState();

    if ((report as Report & { _handledState: { unhandled: boolean } })._handledState.unhandled) {
        // Commenting this out for now because certain on page JS errors can trigger this
        // and I'm not sure we're grouping them in a way where we can tell how often it's happening.
        // But this is going to clear out a user's deal and that almost seems worse than crashing... and crashing would
        // be the rare case, most errors the app should be able to chug along thru just fine since we poll and the data will
        // fix itself.
        //This was brought to attention as a problem when OS broke on a demo site due to an on page analytics error....
        // void store.dispatch(openSubModal({ modalType: "unexpectedErrorModal" }));
    }

    if (!notifyReleaseStages.includes(currentStore.onlineShopper.env)) {
        report.ignore();
        return;
    }

    if (ERRORS_TO_IGNORE.includes(report.errorMessage)) {
        report.ignore();
        return;
    }

    if (report.severity !== "info") {
        report.updateMetaData("store", errorStateSanitizer(currentStore));
        report.updateMetaData("actions", actionLog.getLog()?.actions);
        report.updateMetaData("app", {
            referrer: document.referrer,
            srpLink: currentStore.onlineShopper.srpLink,
        });
    }

    // Bugsnag won't group properly if this message includes variable data.
    if (report.errorMessage.includes("duplicate vin")) {
        report.groupingHash = "Collection can't contain duplicate vin";
    } else {
        report.groupingHash = report.errorMessage;
    }
};

export const log = (
    severity: "info" | "warning" | "error",
    error: Error | string,
    errorInfo?: object,
    opts?: INotifyOpts
) => {
    if (bugsnagClient) {
        switch (severity) {
            case "info":
                if (typeof error !== "string") {
                    return bugsnagClient.notify(error, {
                        metaData: { "error info": errorInfo },
                        severity,
                        ...(opts || {}),
                    });
                }

                const infoSplit = error.split(" - ");

                if (infoSplit.length === 2) {
                    return bugsnagClient.notify(
                        { errorClass: infoSplit[0].trim(), errorMessage: infoSplit[1].trim() },
                        {
                            metaData: { "error info": errorInfo },
                            severity,
                            ...(opts || {}),
                        }
                    );
                }

                return bugsnagClient.notify(
                    { errorClass: "Info", errorMessage: infoSplit[0].trim() },
                    {
                        metaData: { "error info": errorInfo },
                        severity,
                        ...(opts || {}),
                    }
                );
            default:
                return bugsnagClient.notify(typeof error === "string" ? new Error(error) : error, {
                    metaData: { "error info": errorInfo },
                    severity,
                    ...(opts || {}),
                });
        }
    }

    return false;
};

export const logInfo = (message: string, messageInfo?: object, opts?: INotifyOpts) => {
    if (process.env.NODE_ENV !== "test") {
        console.log(message);
    }

    return log("info", message, messageInfo, opts);
};

export const logWarn = (error: Error | string, errorInfo?: object, opts?: INotifyOpts) => {
    if (process.env.NODE_ENV !== "test") {
        console.warn(error);
    }

    return log("warning", error, errorInfo, opts);
};

export const logError = (error: Error | string, errorInfo?: object, opts?: INotifyOpts) => {
    if (process.env.NODE_ENV !== "test") {
        console.error(error);
    }

    return log("error", error, errorInfo, opts);
};

export const initBugsnag = (appVersion: string, store: Store): Client => {
    const currentStore = store.getState();

    bugsnagClient = bugsnag({
        apiKey: BUGSNAG_APP_ID,
        appType: "web",
        appVersion,
        beforeSend: loadStateBeforeErrorSubmit(store),
        // notifyReleaseStages: notifyReleaseStages,
        releaseStage: currentStore.onlineShopper.env,
    });

    bugsnagClient.use(bugsnagReact, React);

    return bugsnagClient;
};
